import React, { useState, useEffect, useMemo } from 'react';
import { getStatsDepartments } from '../../core/constants/departments';
import { useDepartments } from '../../core/DepartmentsContext';

const StatsFilterOptions = ({ sardines, allEventsData, allOpenCallsData, onFilteredSorted }) => {
  const { departments: catalog } = useDepartments();
  const statsConfig = useMemo(() => getStatsDepartments(catalog), [catalog]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOption, setSortOption] = useState('default');

  const sortSardines = (sardines, option) => {
    if (option === 'default') return sardines;

    return [...sardines].sort((a, b) => {
      switch(option) {
        case 'mo-desc':
        // If MO counts are equal, sort by VMO count (desc)
        if (b.stats.eventsAsMO === a.stats.eventsAsMO) {
          if (b.stats.eventsAsVMO === a.stats.eventsAsVMO) {
            return b.stats.totalEvents - a.stats.totalEvents;
          }
          return b.stats.eventsAsVMO - a.stats.eventsAsVMO;
        }
        return b.stats.eventsAsMO - a.stats.eventsAsMO;

        case 'mo-asc':
        // If MO counts are equal, sort by VMO count (asc)
        if (a.stats.eventsAsMO === b.stats.eventsAsMO) {
          if (a.stats.eventsAsVMO === b.stats.eventsAsVMO) {
            return a.stats.totalEvents - b.stats.totalEvents;
          }
          return a.stats.eventsAsVMO - b.stats.eventsAsVMO;
        }
        return a.stats.eventsAsMO - b.stats.eventsAsMO;

        case 'vmo-desc':
        // If VMO counts are equal, sort by MO count (desc)
        if (b.stats.eventsAsVMO === a.stats.eventsAsVMO) {
          if (b.stats.eventsAsMO === a.stats.eventsAsMO) {
            return b.stats.totalEvents - a.stats.totalEvents;
          }
          return b.stats.eventsAsMO - a.stats.eventsAsMO;
        }
        return b.stats.eventsAsVMO - a.stats.eventsAsVMO;

        case 'vmo-asc':
        // If VMO counts are equal, sort by MO count (asc)
        if (a.stats.eventsAsVMO === b.stats.eventsAsVMO) {
          if (a.stats.eventsAsMO === b.stats.eventsAsMO) {
            return a.stats.totalEvents - b.stats.totalEvents;
          }
          return a.stats.eventsAsMO - b.stats.eventsAsMO;
        }
        return a.stats.eventsAsVMO - b.stats.eventsAsVMO;

        case 'total-desc':
        // If total counts are equal, sort by MO count (desc)
        if (b.stats.totalEvents === a.stats.totalEvents) {
          if (b.stats.eventsAsMO === a.stats.eventsAsMO) {
            return b.stats.eventsAsVMO - a.stats.eventsAsVMO;
          }
          return b.stats.eventsAsMO - a.stats.eventsAsMO;
        }
        return b.stats.totalEvents - a.stats.totalEvents;

        case 'total-asc':
        // If total counts are equal, sort by MO count (asc)
        if (a.stats.totalEvents === b.stats.totalEvents) {
          if (a.stats.eventsAsMO === b.stats.eventsAsMO) {
            return a.stats.eventsAsVMO - b.stats.eventsAsVMO;
          }
          return a.stats.eventsAsMO - b.stats.eventsAsMO;
        }
        return a.stats.totalEvents - b.stats.totalEvents;

        default:
        return 0;
      }
    });
  };

  function filterByDateRange(items, getDate, start, end) {
    return items.filter(item => {
      const dateValue = getDate(item);
      if (!dateValue) return false;
      const eventDate = new Date(dateValue);

      if (start && !end) return eventDate >= start;
      if (!start && end) return eventDate <= end;
      if (start && end) return eventDate >= start && eventDate <= end;

      return true;
    });
  }

  // Effect to filter events based on date range
  useEffect(() => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    // Default times
    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);

    const filtered = sardines.map(sardine => {
      const userEvents = allEventsData[sardine._id] || [];
      const filteredEvents = filterByDateRange(userEvents, event => event.date, start, end);

      const userOpenCalls = allOpenCallsData[sardine._id] || [];
      const filteredOpenCalls = filterByDateRange(userOpenCalls, openCall => openCall.event?.date, start, end);

      let shotsWon = 0;
      for (const openCall of filteredOpenCalls) {
        if (openCall.winners.includes(sardine._id)) {
          shotsWon++;
        }
      }

      const calculateStats = (userId, eventsData) => {
        const eventTypeCounts = Object.fromEntries(
          statsConfig.named.map((dept) => [dept.statsKey, 0])
        );
        eventTypeCounts[statsConfig.otherStatsKey] = 0;

        let eventsAsMO = 0;
        let eventsAsVMO = 0;
        let eventsAsStaff = 0;

        eventsData.forEach(event => {
          // Check if user is MO
          if (event.mos && event.mos.some(mo => mo.toString() === userId.toString())) {
            eventsAsMO++;
          }
          // Check if user is VMO (either in vmo field or vmos array)
          else if (
            (event.vmo && event.vmo.toString() === userId.toString()) ||
            (event.vmos && event.vmos.some(vmo => vmo.toString() === userId.toString()))
          ) {
            eventsAsVMO++;
          }
          // Check if user is a regular participant
          else if (event.participants && event.participants.some(participant =>
            participant.toString() === userId.toString())
          ) {
            eventsAsStaff++;
          }

          const department = event.departments?.[0];
          const statsKey = department
            ? statsConfig.getStatsKeyForDepartment(department)
            : statsConfig.otherStatsKey;
          eventTypeCounts[statsKey]++;
        });

        return {
          eventsAsMO,
          eventsAsVMO,
          eventsAsStaff,
          totalEvents: eventsAsMO + eventsAsVMO + eventsAsStaff,
          ...eventTypeCounts,
          shotsWon: shotsWon,
          shotsSent: filteredOpenCalls.length
        };
      };

      // Recalculate stats based on filtered events
      const filteredStats = calculateStats(sardine._id, filteredEvents);

      return {
        ...sardine,
        stats: filteredStats
      };
    });

    // Get sorted sardines
    const sortedSardines = sortSardines(filtered, sortOption);
    onFilteredSorted(sortedSardines);
  }, [startDate, endDate, sortOption, sardines, allEventsData, allOpenCallsData, onFilteredSorted, statsConfig]);

  const handleStartDateChange = e => setStartDate(e.target.value);
  const handleEndDateChange = e => setEndDate(e.target.value);
  const handleSortChange = e => setSortOption(e.target.value);
  const resetDateFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  if (sardines.length === 0) {
    return (
      <div className="page-container">
        <h1 className='page-title'>No sardines selected for comparison</h1>
        <p style={{ textAlign: 'center' }}>Please go back and select sardines to compare.</p>
      </div>
    );
  }

  return (
    <div className="sort-filter-container">
      <div className="sort-filter-options">
        <div className="sort-filter">
          <label className="filter-label">Sort By:</label>
          <select
            className="sort-select"
            value={sortOption}
            onChange={handleSortChange}
          >
            <option value="default">Default Order</option>
            <option value="mo-desc">MO Events (High to Low)</option>
            <option value="mo-asc">MO Events (Low to High)</option>
            <option value="vmo-desc">VMO Events (High to Low)</option>
            <option value="vmo-asc">VMO Events (Low to High)</option>
            <option value="total-desc">Total Events (High to Low)</option>
            <option value="total-asc">Total Events (Low to High)</option>
          </select>
        </div>

        <div>
          <label className="filter-label">Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="date-input"
          />
        </div>
        <div>
          <label className="filter-label">End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className="date-input"
          />
        </div>
        <button
          onClick={resetDateFilters}
          className="button reset-button"
        >
          Reset Filters
        </button>

      </div>
      {(startDate || endDate) && (
        <div className="filter-status">
          Showing events {startDate ? `from ${startDate}` : 'from the beginning'}
          {endDate ? ` to ${endDate}` : ' to the present'}
        </div>
      )}
    </div>
  );
};

export default StatsFilterOptions;
